import { hasLowerLevel, levelAt, mapInfo, radarToWorld, siteBox, type Level, type MapId } from "@69ly/maps";
import { SITES, type Execution, type PlantSpot, type Site } from "@69ly/shared";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { useImageViewer } from "../components/ImageViewer.tsx";
import { BombTimer, PlantOnMap, SiteOutline, type PlantLayers } from "../components/PlantBits.tsx";
import { Avatar } from "../components/Avatar.tsx";
import { focusOn, OnMap, Radar } from "../components/Radar.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useDeletePlantSpot, useExecutions, usePlantSpots } from "../lib/teamplay-queries.ts";
import { MapBar, useMapParam } from "./MapView.tsx";
import { StuffLink } from "../components/StuffDrawer.tsx";
import { ZoomShot } from "../components/StuffMedia.tsx";

/** All the points of a site's plant spots: what the zoomed map must show. */
function sitePoints(plants: PlantSpot[]) {
  return plants.flatMap((p) => [
    p.plant,
    ...p.positions,
    ...p.utility.flatMap((u) => [u.stuff.landing]),
  ]);
}

function PlantPanel({ map, plant, executions, retakes }: { map: MapId; plant: PlantSpot; executions: Execution[]; retakes: Execution[] }) {
  const { t } = useTranslation();
  const del = useDeletePlantSpot();
  const [, setParams] = useSearchParams();
  const viewer = useImageViewer(plant.media.map((m) => ({ url: m.url, caption: `${plant.name} · ${t(`plantCaption.${m.caption}`)}` })));
  return (
    <aside className="card panel execution-panel" aria-live="polite">
      {viewer.viewer}
      <div>
        <h2 className="panel-title">
          <span className="c4-tag">C4</span> {plant.name}
        </h2>
        <p className="muted small">
          {t("plant.kind.postplant")} · {t(`area.${plant.site}`)} · {t("mapView.by", { name: plant.author.name })}
        </p>
      </div>
      <section>
        <h3 className="eyebrow">{t("plant.wherePlant")}</h3>
        {plant.media.length > 0 ? (
          <div className="plant-gallery">
            {plant.media.map((m, i) => (
              <figure key={m.id}>
                <ZoomShot picture={m} alt={`${t(`plantCaption.${m.caption}`)}: ${plant.name}`} onFullscreen={() => viewer.open(i)} compact />
                <figcaption>{t(`plantCaption.${m.caption}`)}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <p className="muted small">{t("detail.noPictures")}</p>
        )}
      </section>
      <section>
        <h3 className="eyebrow">{t("plant.utility")}</h3>
        {plant.utility.length === 0 ? (
          <p className="muted small">{t("plant.noUtility")}</p>
        ) : (
          <ul className="roles">
            {plant.utility.map((u, i) => (
              <li key={i}>
                <span className={`slot-dot slot-${u.slot}`} />
                <StuffLink id={u.stuff.id} className="roles-role">
                  {u.stuff.title}
                </StuffLink>
                <span className="muted small">{t("plant.playerN", { n: u.slot })}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <h3 className="eyebrow">{t("plant.positions")}</h3>
        {plant.positions.length === 0 ? (
          <p className="muted small">{t("plant.noPositions")}</p>
        ) : (
          <ul className="roles">
            {plant.positions.map((p, i) => (
              <li key={i}>
                <span className={`slot-dot slot-${p.slot}`} />
                <span className="roles-role">{p.label || t("plant.playerN", { n: p.slot })}</span>
                {p.member ? (
                  <span className="pp-member small">
                    <Avatar name={p.member.name} url={p.member.avatarUrl} size={18} />
                    {p.member.name}
                  </span>
                ) : (
                  <span className="muted small">{t("plant.playerN", { n: p.slot })}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      {plant.notes && <p className="notes small">{plant.notes}</p>}
      {executions.length > 0 && (
        <section>
          <h3 className="eyebrow">{t("plant.beforePlant")}</h3>
          <ul className="plant-links small">
            {executions.map((e) => (
              <li key={e.id}>
                <Link to={`/library/${map}/executions?e=${e.id}`}>{e.title}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {retakes.length > 0 && (
        <section>
          <h3 className="eyebrow">{t("plant.theirRetake")}</h3>
          <ul className="plant-links small">
            {retakes.map((e) => (
              <li key={e.id}>
                <Link to={`/library/${map}/executions?e=${e.id}`}>{e.title}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {plant.canEdit && (
        <div className="row">
          <Link to={`/plant-spots/${plant.id}/edit`} className="btn btn-secondary">
            {t("mapView.edit")}
          </Link>
          <button
            type="button"
            className="btn btn-quiet btn-danger"
            disabled={del.isPending}
            onClick={() => {
              if (window.confirm(t("plant.confirmDelete", { name: plant.name })))
                del.mutate(plant.id, {
                  onSuccess: () =>
                    setParams(
                      (p) => {
                        const n = new URLSearchParams(p);
                        n.delete("spot");
                        return n;
                      },
                      { replace: true },
                    ),
                });
            }}
          >
            {t("detail.delete")}
          </button>
        </div>
      )}
      <p className="muted small">
        <Link to={`/library/${map}/learn`}>{t("execution.learnLink")}</Link>
      </p>
    </aside>
  );
}

export function PostPlantPage() {
  const { t } = useTranslation();
  const map = useMapParam();
  const { scope } = useCurrentTeam();
  const plants = usePlantSpots(scope, map ?? "");
  const executions = useExecutions(scope, map ?? "");
  const [params, setParams] = useSearchParams();
  const [layers, setLayers] = useState<PlantLayers>({ utility: true, positions: true, cones: true });
  const [level, setLevel] = useState<Level>("upper");

  const all = useMemo(() => plants.data ?? [], [plants.data]);
  const spotParam = all.find((p) => p.id === params.get("spot"));
  // Post-plants only: retakes are built with the executions now.
  const site: Site = spotParam?.site ?? (params.get("site") === "b" ? "b" : "a");
  const ofKind = all.filter((p) => p.kind === "postplant");
  const onSite = ofKind.filter((p) => p.site === site);
  const selected = spotParam && spotParam.site === site ? spotParam : (onSite[0] ?? null);
  // The executions that lead here, and the CT retakes of the same site.
  const leadIn = (executions.data ?? []).filter((e) => e.kind === "execute" && e.target === site);
  const retakes = (executions.data ?? []).filter((e) => e.kind === "retake" && e.target === site);

  useEffect(() => {
    if (!map) return;
    if (selected) setLevel(levelAt(map, selected.plant.z ?? 0));
    else setLevel(siteBox(map, site)?.level ?? "upper");
  }, [map, selected, site]);

  if (!map) return <Navigate to="/library" replace />;
  if (plants.isPending) return <Loading />;
  if (plants.error) return <ErrorState message={errorText(t, plants.error)} onRetry={() => void plants.refetch()} />;

  const info = mapInfo(map);
  // Zoomed on the site, wide enough for the players and utility around it.
  const box = siteBox(map, site);
  const boxCorners = box ? [radarToWorld(map, { u: box.u0, v: box.v0 }), radarToWorld(map, { u: box.u1, v: box.v1 })] : [];
  const focus = focusOn(map, [...boxCorners, ...sitePoints(selected ? [selected] : onSite)], { maxScale: 3, pad: 0.08 });
  const set = (change: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(change)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  const newLink = (
    <Link to={`/library/${map}/post-plant/new?site=${site}`} className="btn btn-primary">
      <Icon name="plus" />
      {t("plant.new")}
    </Link>
  );

  return (
    <div className="library">
      <MapBar map={map} section="postPlant" count={ofKind.length} countLabel={t("plant.count", { count: ofKind.length })} actions={newLink} />
      <div className="library-body execution-body">
        <nav className="execution-switcher" aria-label={t("plant.switcher")}>
          <div className="segmented" role="group" aria-label={t("plant.site")}>
            {SITES.map((s) => (
              <button key={s} type="button" className="seg" aria-pressed={site === s} onClick={() => set({ site: s, spot: null })}>
                {t(`area.${s}`)}
              </button>
            ))}
          </div>
          <ul className="execution-list">
            {onSite.map((p) => (
              <li key={p.id}>
                <button type="button" className="execution-item" aria-pressed={selected?.id === p.id} onClick={() => set({ spot: p.id, site: null })}>
                  <span className="execution-item-title">
                    <span className="c4-tag">C4</span> {p.name}
                  </span>
                  <span className="muted small">
                    {t("plant.counts", { utility: p.utility.length, positions: p.positions.length })}
                  </span>
                </button>
              </li>
            ))}
            {onSite.length === 0 && <li className="muted small">{t("plant.noneOnSite", { site: t(`areaShort.${site}`) })}</li>}
          </ul>
          <p className="muted small">
            <Link to={`/library/${map}/executions?k=retake`}>{t("plant.retakesMoved")}</Link>
          </p>
        </nav>

        <div className="execution-stage">
          <div className="execution-row">
          <section className="map-frame execution-map" aria-label={info.name}>
            <Radar map={map} mapName={info.name} level={level} focus={focus} dim={onSite.length === 0}>
              <SiteOutline map={map} site={site} level={level} active />
              {selected && <PlantOnMap map={map} plant={selected} level={level} layers={layers} />}
              {onSite.map((p) =>
                levelAt(map, p.plant.z ?? 0) === level ? (
                  <OnMap key={p.id} map={map} x={p.plant.x} y={p.plant.y}>
                    <button
                      type="button"
                      className="c4-marker"
                      aria-pressed={selected?.id === p.id}
                      aria-label={t("plant.c4Label", { name: p.name })}
                      onClick={(e) => {
                        e.stopPropagation();
                        set({ spot: p.id, site: null });
                      }}
                    >
                      <span className="c4-tag">C4</span>
                      {/* The name on hover only: it takes room the players need. */}
                      <span className="c4-name" aria-hidden="true">
                        {p.name}
                      </span>
                    </button>
                  </OnMap>
                ) : null,
              )}
            </Radar>
            {selected && (
              <div className="map-overlay map-overlay-bottom glass">
                <div className="chips" role="group" aria-label={t("plant.layers")}>
                  {(["utility", "positions", "cones"] as const).map((k) => (
                    <button key={k} type="button" className="chip" aria-pressed={layers[k]} onClick={() => setLayers({ ...layers, [k]: !layers[k] })}>
                      {t(`plant.layer.${k}`)}
                    </button>
                  ))}
                </div>
                {hasLowerLevel(map) && (
                  <div className="segmented" role="group" aria-label={t("level.label")}>
                    {(["upper", "lower"] as const).map((l) => (
                      <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                        {t(`level.${l}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {onSite.length === 0 && (
              <div className="glass-card empty-card">
                <h1 className="section-title">{t("plant.emptyTitle", { site: t(`area.${site}`) })}</h1>
                <p>{t("plant.emptyHelp")}</p>
                {newLink}
              </div>
            )}
          </section>
          {selected && <PlantPanel map={map} plant={selected} executions={leadIn} retakes={retakes} />}
          </div>
          {selected && (
            <section className="card timeline" aria-label={t("plant.utility")}>
              <div className="timeline-head">
                <h2 className="eyebrow">{t("plant.bombTimer")}</h2>
                <span className="muted small">{t("plant.bombTimerHelp")}</span>
              </div>
              <BombTimer plant={selected} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
